import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Search, FileWarning, CircleCheck, Clock, Ban } from "lucide-react";
import { toast } from "sonner";

const CAUSA_META = {
  anulada: { label: "Anulada", color: "bg-red-100 text-red-800 border-red-300", icon: Ban },
  rechazada_sri: { label: "Rechazada por SRI", color: "bg-red-100 text-red-800 border-red-300", icon: AlertCircle },
  borrador: { label: "Borrador (no enviada)", color: "bg-yellow-100 text-yellow-800 border-yellow-300", icon: FileWarning },
  reserva_expirada: { label: "Reserva expirada", color: "bg-gray-100 text-gray-700 border-gray-300", icon: Clock },
  reservada_activa: { label: "Reservada ahora mismo", color: "bg-blue-100 text-blue-800 border-blue-300", icon: Clock },
  huerfano: { label: "Número saltado", color: "bg-orange-100 text-orange-800 border-orange-300", icon: FileWarning },
};

export default function AuditoriaSecuencial() {
  const [tipo, setTipo] = useState("factura_electronica");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const buscar = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (desde) params.append("desde", desde);
      if (hasta) params.append("hasta", hasta);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const { data: resp } = await api.get(`/invoicing/gaps-secuencial/${tipo}${qs}`);
      setData(resp);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al consultar");
    } finally { setLoading(false); }
  };

  return (
    <div className="p-4 space-y-4" data-testid="auditoria-secuencial-page">
      <div>
        <h1 className="text-2xl font-semibold text-[#173d34]">Auditoría de secuencial SRI</h1>
        <p className="text-sm text-[#5c7368]">Encuentra por qué se saltaron números en la secuencia de facturación electrónica.</p>
      </div>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Filtros de búsqueda</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Tipo de comprobante</Label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger data-testid="tipo-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="factura_electronica">Facturas</SelectItem>
                <SelectItem value="nota_credito">Notas de Crédito</SelectItem>
                <SelectItem value="retencion">Retenciones</SelectItem>
                <SelectItem value="guia_remision">Guías de Remisión</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Desde N° (opcional)</Label>
            <Input type="number" value={desde} onChange={e => setDesde(e.target.value)} placeholder="ej. 5880" data-testid="desde-input" />
          </div>
          <div>
            <Label className="text-xs">Hasta N° (opcional)</Label>
            <Input type="number" value={hasta} onChange={e => setHasta(e.target.value)} placeholder="ej. 5900" data-testid="hasta-input" />
          </div>
          <div className="flex items-end">
            <Button onClick={buscar} disabled={loading} className="w-full bg-[#173d34]" data-testid="buscar-btn">
              <Search className="w-4 h-4 mr-2" />{loading ? "Buscando..." : "Buscar huecos"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {data && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-3 flex-wrap">
              <span>Rango {data.rango?.desde} — {data.rango?.hasta}</span>
              <Badge variant="outline">Autorizados: {data.total_docs}</Badge>
              <Badge className={data.total_gaps > 0 ? "bg-orange-100 text-orange-800 border-orange-300" : "bg-green-100 text-green-800"}>
                Huecos: {data.total_gaps}
              </Badge>
              <Badge variant="outline">Máx secuencial actual: {data.max_secuencial}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.gaps.length === 0 ? (
              <div className="text-center py-8 text-[#5c7368] flex flex-col items-center gap-2">
                <CircleCheck className="w-10 h-10 text-green-600" />
                <span>Sin huecos en este rango. La secuencia está completa.</span>
              </div>
            ) : (
              <div className="border rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#f7f9f8] text-xs uppercase text-[#5c7368]">
                    <tr>
                      <th className="text-left p-2">Número</th>
                      <th className="text-left p-2">Causa</th>
                      <th className="text-left p-2">Fecha</th>
                      <th className="text-left p-2">Cliente</th>
                      <th className="text-right p-2">Total</th>
                      <th className="text-left p-2">Nota</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.gaps.map((g, i) => {
                      const meta = CAUSA_META[g.causa] || { label: g.causa, color: "bg-gray-100 text-gray-700", icon: AlertCircle };
                      const Icon = meta.icon;
                      return (
                        <tr key={i} className="border-t border-[#e6e0d0]" data-testid={`gap-row-${g.numero}`}>
                          <td className="p-2 font-mono text-xs">{g.numero_formateado}</td>
                          <td className="p-2">
                            <Badge className={`${meta.color} border`}><Icon className="w-3 h-3 mr-1" />{meta.label}</Badge>
                          </td>
                          <td className="p-2 text-xs text-[#5c7368]">{g.fecha || "—"}</td>
                          <td className="p-2 text-xs">{g.cliente || "—"}</td>
                          <td className="p-2 text-right text-xs">{g.total ? `$${g.total.toFixed(2)}` : "—"}</td>
                          <td className="p-2 text-xs text-[#5c7368]">{g.nota}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="bg-[#f7f9f8]">
        <CardHeader className="pb-2"><CardTitle className="text-sm">📘 Guía rápida</CardTitle></CardHeader>
        <CardContent className="text-xs text-[#5c7368] space-y-1">
          <p>• <b>Anulada</b>: la factura existe pero fue anulada. Su número no se reutiliza (norma SRI).</p>
          <p>• <b>Rechazada por SRI</b>: el SRI devolvió error. Revisar el comprobante y re-emitir con nuevo número.</p>
          <p>• <b>Borrador</b>: guardada pero no enviada al SRI. Aún puedes editarla y firmarla.</p>
          <p>• <b>Reserva expirada</b>: el usuario abrió &quot;nueva factura&quot;, el número se reservó pero no se guardó. El número queda como hueco.</p>
          <p>• <b>Reservada ahora mismo</b>: otro usuario tiene abierto ese número. Aparecerá cuando la guarde.</p>
          <p>• <b>Número saltado</b>: no existe en la DB. Suele ser una reserva expirada muy antigua o un error histórico. <b>Se puede regularizar re-emitiendo el próximo número con nota explicativa</b>, o dejar el hueco (el SRI acepta huecos siempre que la numeración sea ascendente).</p>
        </CardContent>
      </Card>
    </div>
  );
}
